/**
 * PerVisitBillingOverride — Owner/admin one-off billing override for a
 * single work order. Empty override means "inherit the customer's
 * account setting" (lead.billingMode).
 *
 * Backend contract: PUT /api/tasks/{taskId}/charge-override with
 *   chargeOverride ∈ 'inherit' | 'auto_charge' | 'invoice_auto_send' | 'invoice_manual'
 *
 * The technician role does NOT see this control (spec: "Do not expose
 * financial controls to technician roles unless already permitted by the
 * existing authorization model.").
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import { useCompany } from '../../context/CompanyContext';
import { useRole } from '../../context/RoleContext';
import { auth } from '../../firebase';

const OPTIONS = [
  { value: 'inherit',           label: 'Inherit account setting' },
  { value: 'auto_charge',       label: 'Auto-charge card on file' },
  { value: 'invoice_auto_send', label: 'Send invoice by SMS' },
  { value: 'invoice_manual',    label: 'Manual collection (no send)' },
];

const PerVisitBillingOverride = ({ taskId, currentValue, onChanged }) => {
  const { companyId } = useCompany();
  const { isAdmin, isTechnician } = useRole();
  const [saving, setSaving] = useState(false);
  const [value, setValue]   = useState(currentValue || 'inherit');

  if (!taskId || !isAdmin || isTechnician) return null;

  const onChange = async (next) => {
    setSaving(true);
    setValue(next);
    try {
      const token = await auth.currentUser?.getIdToken(false);
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/tasks/${taskId}/charge-override`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ companyId, chargeOverride: next }),
        },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
      toast.success(
        next === 'inherit'
          ? 'This visit will use the account billing setting.'
          : `This visit only: ${OPTIONS.find(o => o.value === next)?.label}`,
      );
      onChanged?.(next);
    } catch (err) {
      toast.error(err.message || 'Could not update billing override');
      setValue(currentValue || 'inherit');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs"
      data-testid="per-visit-billing-override"
    >
      <label className="text-[11px] uppercase tracking-wide font-semibold text-gray-500 mb-1 block">
        Billing for this visit
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={saving}
        className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 bg-white disabled:opacity-60"
        data-testid="per-visit-billing-override-select"
      >
        {OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
};

export default PerVisitBillingOverride;
